import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad är B2B-video?",
    a: "B2B-video är rörligt innehåll riktat mot företag och beslutsfattare snarare än konsumenter — exempelvis kundcasefilm, produktdemos, LinkedIn-video, employer branding och säljmaterial. Syftet är oftast att korta säljcykeln, bygga förtroende och förklara komplexa erbjudanden snabbare än text.",
  },
  {
    q: "Vilken B2B-video ger bäst effekt?",
    a: "För de flesta B2B-bolag levererar kundcasefilm och produktdemos högst avkastning eftersom de adresserar köparens två största frågor: 'fungerar det?' och 'fungerar det för någon som oss?'. LinkedIn-video och thought leadership bygger pipeline över tid, medan employer branding-film stärker rekryteringen.",
  },
  {
    q: "Hur ofta bör ett B2B-bolag publicera video?",
    a: "För att bygga igenkänning räcker det sällan med en film per år. Marknadsavdelningar som vill driva pipeline publicerar typiskt 4–12 klipp per månad — en blandning av case, produkt, kunskap och kortformat. Det är därför många B2B-bolag väljer ett abonnemang i stället för enstaka projekt.",
  },
  {
    q: "Vad kostar B2B-video?",
    a: "En enskild B2B-produktion (kundcase, produktfilm, intervju) ligger typiskt på 40 000–150 000 kr beroende på omfattning. Vid löpande behov sänker ett filmabonnemang som Fenny Pulse kostnaden per film — från 14 995 kr/mån — eftersom strategi, inspelning och versionering återanvänds över flera leveranser.",
  },
  {
    q: "Kan ni leverera video i alla format vi behöver?",
    a: "Ja. Varje produktion versioneras till de format ni faktiskt använder: 16:9 för sajt och YouTube, 9:16 för Reels och TikTok, 1:1 och 4:5 för LinkedIn och Instagram, plus undertexter. En inspelning blir på så sätt 3–5 färdiga filmer för olika kanaler.",
  },
  {
    q: "Hur snabbt kan vi komma igång?",
    a: "Efter ett kostnadsfritt strategimöte planerar vi första produktionen direkt. Med Pulse-abonnemang levereras första klippet inom 10–14 dagar. För enskilda projekt är typisk ledtid 3–5 veckor från brief till färdig film.",
  },
];

export const Route = createFileRoute("/b2b-video")({
  head: () => ({
    meta: [
      { title: "B2B-video — film som driver pipeline | Fenny" },
      {
        name: "description",
        content:
          "B2B-video för marknadsavdelningar och säljteam: kundcasefilm, produktdemos, LinkedIn-video och employer branding. Strategi, produktion och versionering .",
      },
      { property: "og:title", content: "B2B-video — Fenny" },
      { property: "og:description", content: "Film som kortar säljcykeln och driver pipeline för B2B — case, produkt, LinkedIn och employer branding." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/b2b-video" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/b2b-video" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/b2b-video", name: "B2B-video — film som driver pipeline | Fenny", description: "B2B-video för marknadsavdelningar och säljteam: kundcasefilm, produktdemos, LinkedIn-video och employer branding. Strategi, produktion och versionering ."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "B2B-video",
            path: "/b2b-video",
            serviceType: "Produktion av B2B-video",
            description:
              "B2B-video för marknadsavdelningar och säljteam: kundcasefilm, produktdemos, LinkedIn-video, employer branding och säljmaterial. Strategi, produktion och versionering.",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "B2B-video", path: "/b2b-video" },
          ]),
        ),
      },
    ],
  }),
  component: B2bVideoPage,
});

function B2bVideoPage() {
  return (
    <LandingPage
      eyebrow="B2B-video"
      title={<>B2B-video som kortar säljcykeln — inte fyller arkivet.</>}
      lede="Fenny producerar B2B-video för marknadsavdelningar och säljteam: kundcasefilm, produktdemos, LinkedIn-video och employer branding. Film som hjälper komplexa erbjudanden att förklaras snabbare och bygger förtroende hos beslutsfattare."
      tldr={
        <p>
          B2B-video är film riktad mot företag och beslutsfattare i stället för
          konsumenter — kundcase, produktdemos, LinkedIn-video, employer branding
          och säljmaterial. Fenny levererar B2B-video per projekt (typiskt{" "}
          <strong>40 000–150 000 kr</strong>) eller löpande via{" "}
          <strong>Pulse-abonnemang från 14 995 kr/mån</strong> med versionering
          till alla format. Första klippet inom 10–14 dagar.
        </p>
      }
      ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
      ctaSecondary={{ label: "Räkna pris", to: "/priser" }}
      trackingId="b2b-video"
      relatedLinksFor="/b2b-video"
      quickAnswerId="sv_hur_anvanda_video_b2b"
      bullets={[
        { title: "Köparens frågor först", body: "Vi bygger film kring de två frågor varje B2B-köpare ställer: fungerar det, och fungerar det för någon som oss?" },
        { title: "Pipeline över tid", body: "Löpande case, produkt och thought leadership i stället för en film per år. Volym och regelbundenhet driver inbound." },
        { title: "Alla format ingår", body: "Sajt, YouTube, LinkedIn, Reels och säljpresentation — en inspelning blir 3–5 färdiga filmer." },
      ]}
      sections={[
        {
          id: "anvandningsomraden",
          heading: "Vilken B2B-video ger effekt?",
          body: (
            <>
              <p>
                Kundcasefilm och produktdemos levererar oftast högst avkastning —
                de adresserar köparens två viktigaste frågor direkt.{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/kundcase-film">Kundcasefilm</a>{" "}
                bygger socialt bevis, medan{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/produktfilm">produktfilm</a>{" "}
                gör komplexa lösningar begripliga på under två minuter.
              </p>
              <p>
                Längre fram driver{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/film-for-linkedin">film för LinkedIn</a>{" "}
                och thought leadership pipeline, och{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/employer-branding-film">employer branding-film</a>{" "}
                stärker rekryteringen i konkurrensutsatta roller.
              </p>
            </>
          ),
        },
        {
          id: "lopande",
          heading: "Varför väljer B2B-bolag abonnemang?",
          body: (
            <p>
              En film per år bygger sällan igenkänning. Marknadsavdelningar som
              vill driva pipeline behöver 4–12 klipp per månad — en mix av case,
              produkt, kunskap och kortformat. Det är därför många B2B-bolag väljer
              ett{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmabonnemang">filmabonnemang</a>{" "}
              i stället för enstaka projekt: förutsägbar budget, kortare ledtid och
              en strategi som rullar över tid. Läs mer om{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/pulse">Pulse</a>.
            </p>
          ),
        },
        {
          id: "pris",
          heading: "Vad kostar B2B-video?",
          body: (
            <p>
              En enskild produktion ligger typiskt på 40 000–150 000 kr beroende på
              omfattning. Vid löpande behov sänker abonnemang kostnaden per film.{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/priser">Räkna fram er nivå direkt</a>{" "}
              eller jämför{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/jamfor/filmproduktion-abonnemang-vs-projektpris">abonnemang mot projektpris</a>.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseLimit={6}
      closing={{
        title: "Dags att bygga en videomotor för B2B?",
        body: "30 minuter där vi pekar ut tre filmer som driver pipeline snabbast i er pipeline.",
        primary: { label: "Boka strategimöte", to: "/kontakt" },
        secondary: { label: "Om filmabonnemang", to: "/filmabonnemang" },
      }}
    />
  );
}
